import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Alert,
  AlertTitle,
  Paper,
  Link
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Diamond as DiamondIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Apple as AppleIcon,
  Info as InfoIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import { useSubscription } from '../contexts/SubscriptionContext';

interface SubscriptionModalProps {
  open: boolean;
  onClose: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ open, onClose }) => {
  const { isLoading } = useSubscription();

  const premiumFeatures = [
    'Unlimited test history',
    'Ad-free experience', 
    'Advanced analytics',
    'Export & share results',
    'Priority customer support'
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth 
      maxWidth="sm"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <DiamondIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Premium Subscription</Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Subscribe via Mobile App</AlertTitle>
          For legal compliance with Apple and Google payment policies, subscriptions 
          are only available through our mobile apps. Your subscription will automatically 
          sync across all your devices when you sign in with the same account.
        </Alert>
        
        <Typography variant="body1" paragraph>
          Upgrade to Premium for unlimited access to all features and an enhanced experience.
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
          Premium includes:
        </Typography>
        
        <List dense>
          {premiumFeatures.map((feature, index) => (
            <ListItem key={index} disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={feature} />
            </ListItem>
          ))}
        </List>
        
        <Divider sx={{ my: 2 }} />
        
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            backgroundColor: 'rgba(41, 182, 246, 0.1)', 
            borderRadius: 2,
            border: '1px solid rgba(41, 182, 246, 0.3)',
            textAlign: 'center',
            mb: 2
          }}
        >
          <Typography variant="h6" gutterBottom color="primary">
            Subscribe using your mobile device
          </Typography>
          
          <Typography variant="body2" paragraph>
            To subscribe, please download and install the Amsler Grid Daily app on your iOS or Android device.
            Your subscription will automatically sync across all your devices using the same account.
          </Typography>
          
          <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AppleIcon sx={{ fontSize: 40, mb: 1, color: '#000' }} />
                  <Typography variant="h6" gutterBottom>
                    iOS App
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Download from the App Store
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<AppleIcon />}
                    component={Link}
                    href="https://apps.apple.com/app/amslergriddaily"
                    target="_blank"
                  >
                    App Store
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <PhoneAndroidIcon sx={{ fontSize: 40, mb: 1, color: '#3ddc84' }} />
                  <Typography variant="h6" gutterBottom>
                    Android App
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Download from Google Play
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<PhoneAndroidIcon />}
                    component={Link}
                    href="https://play.google.com/store/apps/details?id=com.amslergriddaily"
                    target="_blank"
                  >
                    Google Play
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <InfoIcon color="info" fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              After subscribing, return to this web app and your premium status will automatically sync.
            </Typography>
          </Box>
        </Paper>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionModal; 